import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Table from '@risksmart-app/components/src/table';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportButton from 'src/components/export-button';
import { PageLayout } from 'src/layouts';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useGetConsequenceRegister } from '@/hooks/queries/consequence/useGetConsequenceRegister';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';
import { getCounter } from '@/utils/collectionUtils';

import ConsequenceModal from '../issues/update/tabs/consequences/ConsequenceModal';
import { useGetRegisterTableProps } from './config';
import { ConsequenceRegisterToolbar } from './ConsequenceRegisterToolbar';
import type { ConsequenceFlatField, ConsequenceRegisterFields } from './types';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

const Page: FC = () => {
  const [openConsequence, setOpenConsequence] =
    useState<ConsequenceFlatField | null>(null);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'consequences',
  });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const { data, loading, refetch } = useGetConsequenceRegister({
    queryArgs: {},
  });

  const tableProps = useGetRegisterTableProps(
    data?.consequence,
    (consequence: ConsequenceRegisterFields) => setOpenConsequence(consequence)
  );

  const title = st('register_title');
  const counter = getCounter(tableProps.totalItemsCount, loading);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  return (
    <PageLayout
      helpTranslationKey={'consequences.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-160) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <ConsequenceRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.Consequence}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table {...tableProps} loading={loading} />
      {openConsequence && (
        <ConsequenceModal
          consequenceId={openConsequence.Id}
          onDismiss={(saved) => {
            setOpenConsequence(null);
            if (saved) {
              refetch();
            }
          }}
          issueId={openConsequence.ParentIssueId}
        />
      )}
    </PageLayout>
  );
};

export default Page;
